import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, BookOpen, Star, Download } from "lucide-react";
import { toast } from "sonner";

const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];

function Row({ icon: Icon, color, label, entry, unit, term, type }) {
  const { L } = useLang();
  const dl = async () => {
    try {
      const r = await api.raw.get(`/students/${entry.student_id}/honour-certificate?term=${encodeURIComponent(term)}&type=${type}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "certificat_honneur.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  return (
    <div className="flex items-center gap-3 py-2">
      <span className={`h-9 w-9 rounded-full grid place-items-center ${color}`}><Icon className="h-4 w-4 text-white" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {entry ? <p className="font-semibold truncate">{entry.name} <span className="text-primary">· {entry.value}{unit}</span></p>
          : <p className="text-sm text-muted-foreground italic">{L("Not awarded yet", "Pas encore décerné")}</p>}
      </div>
      {entry && entry.student_id && (
        <button onClick={dl} data-testid={`hr-cert-${type}-${entry.student_id}`} title={L("Download certificate", "Télécharger le certificat")} className="text-amber-600 hover:text-amber-800 shrink-0"><Download className="h-4 w-4" /></button>
      )}
    </div>
  );
}

export default function HonourRoll() {
  const { L } = useLang();
  const [term, setTerm] = useState("Term 1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setData(await api.get("/honour-roll", { params: { term } })); } catch (e) { setData(null); }
      setLoading(false);
    })();
  }, [term]);

  const classes = data?.classes || [];

  return (
    <div className="animate-fade-up" data-testid="honour-roll">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-extrabold text-2xl flex items-center gap-2"><Trophy className="h-6 w-6 text-amber-500" />{L("Honour Roll", "Tableau d'Honneur")}</h2>
          <p className="text-muted-foreground text-sm">{L("Top grade, top reader and top project in each class.", "Meilleure note, meilleur lecteur et meilleur projet de chaque classe.")}</p>
        </div>
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="w-40" data-testid="hr-term-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <Empty title="…" /> : classes.length === 0 ? (
        <Empty title={L("Nothing to celebrate yet", "Rien à célébrer pour l'instant")} hint={L("Honours appear once grades and reading are entered for this term.", "Les honneurs apparaissent une fois les notes et lectures saisies pour ce terme.")} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {classes.map((c) => (
            <div key={c.class_id} className="bg-card border border-border rounded-lg p-5" data-testid={`hr-class-${c.class_id}`}>
              <p className="font-heading font-bold text-lg mb-2">{c.class_name}</p>
              <div className="divide-y divide-border">
                <Row icon={Star} color="bg-primary" label={L("Highest grade", "Meilleure note")} entry={c.top_grade} unit="/20" term={term} type="grade" />
                <Row icon={BookOpen} color="bg-emerald-500" label={L("Top reader (books)", "Meilleur lecteur (livres)")} entry={c.top_reader} unit={L(" books", " livres")} term={term} type="reader" />
                <Row icon={Trophy} color="bg-amber-500" label={L("Best project", "Meilleur projet")} entry={c.top_project} unit="/100" term={term} type="project" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
